import { Box, Button } from '@factorialco/deprecated-design-system'
import {
  AtsCreateApplicationPhaseDocument,
  AtsGetHiringPipelinePhasesDocument,
} from 'generated/resources/graphql'
import { maxBy } from 'lodash'

import Modal from 'modules/core/components/Modal'
import TextField from 'modules/core/components/TextField'
import { useForm } from 'modules/core/hooks/useForm'
import type { FormErrors } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { UNASSIGNED_STAGE, getPhaseType } from 'modules/ats/lib/utils/hiringStages'
import { HiringStageWithApplicationPhases } from 'modules/ats/pages/JobPostingSettings/HiringPipeline/utils'

interface FormValues {
  name: string
}

interface Props {
  jobId: number
  hiringStage: HiringStageWithApplicationPhases
  onClose: () => void
}

export const AddApplicationPhaseModal = ({ onClose, jobId, hiringStage }: Props) => {
  const [createAtsApplicationPhase, { loading }] = useMutation(AtsCreateApplicationPhaseDocument, {
    extractorFn: (data) => data.atsMutations.createAtsApplicationPhase,
    refetchQueries: [AtsGetHiringPipelinePhasesDocument],
    awaitRefetchQueries: true,
  })

  const form = useForm<FormValues>({
    defaultValues: {
      name: '',
    },
    validate: (data) => {
      const errors: FormErrors<FormValues> = {}

      if (!data.name) {
        errors.name = i18n.t(
          'ats.job_posting_settings.hiring_pipeline.add_phase_modal.phase_input_error'
        )
      }

      return errors
    },
    onSubmit: async (data, { setErrors }) => {
      const lastPosition =
        maxBy(hiringStage.applicationPhases, (phase) => phase.position)?.position || 0

      const result = await createAtsApplicationPhase({
        variables: {
          name: data.name,
          atsHiringStageId: hiringStage.id,
          atsJobPostingId: jobId,
          phaseType: getPhaseType(hiringStage.name === UNASSIGNED_STAGE ? 'new' : hiringStage.name),
          position: lastPosition + 1,
        },
      })

      const handled = handleResult(result, (errors) => {
        setErrors(errors)
      })

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('common.saved_successfully'),
        })
        onClose()
      }

      return handled
    },
  })

  return (
    <Modal
      title={i18n.t('ats.job_posting_settings.hiring_pipeline.add_phase_modal.title')}
      description={i18n.t('ats.job_posting_settings.hiring_pipeline.add_phase_modal.description')}
      illustration="automations"
      onClose={onClose}
      onSubmit={() => form.handleSubmit()}
      footer={
        <Box flexDirection="row" gap="s16">
          <Button type="secondary" onClick={() => onClose()}>
            {i18n.t('common.cancel')}
          </Button>
          <Button type="negative" submit>
            {i18n.t('common.save')}
          </Button>
        </Box>
      }
    >
      <TextField
        label={i18n.t('ats.job_posting_settings.hiring_pipeline.phase_input_label')}
        value={form.values.name}
        onChange={(value) => form.setValues({ ...form.values, name: value })}
        error={form.touched.name ? form.errors.name : undefined}
        mandatory
      />
    </Modal>
  )
}
